import { gql } from '@apollo/client';

import { formatCurrency, formatPercent } from '@/lib/utils';

export const GET_ALLOCATION_HISTORY = gql`
  query GetAllocationHistory($opportunityId: ID!) {
    allocationHistory(opportunityId: $opportunityId) {
      id
      allocationAmount
      debtAllocationAmount
      equityAllocationAmount
      version
      status
      allocationType
      createdDate
      totalAllocated
      numberOfFunds
      hasWarnings
      hasOverrides
      isFulfilled
    }
  }
`;

export const GET_ALLOCATION_DETAILS = gql`
  query GetAllocationDetails($id: ID!) {
    node(id: $id) {
      ... on LiquidityAllocationNode {
        id
        opportunityId
        allocationAmount
        debtAllocationAmount
        equityAllocationAmount
        version
        status
        allocationType
        useOfProceeds
        hasOverrides
        finopsReviewed
        createdDate
        overrideNotes
        finopsReviewDate
        displayName
        totalAllocated
        numberOfFunds
        hasWarnings
        isFulfilled
        allocationLines {
          id
          fundId
          allocatedAmount
          debtAllocatedAmount
          equityAllocatedAmount
          fairShareAmount
          fairShareDebtAmount
          fairShareEquityAmount
          concentrationLimitPct
          concentrationLimitAmount
          isExcluded
          isCapped
          customAllocation
          isOverLimit
          excessAmount
          allocationPercentage
          overrideType
          fund {
            id
            name
            balance
            buyingPower
            concentrationLimit
          }
        }
        warnings {
          id
          fundId
          warningType
          message
          severity
          fund {
            id
            name
          }
        }
      }
    }
  }
`;

export const SUBMIT_FOR_REVIEW = gql`
  mutation SubmitForReview($allocationId: ID!) {
    submitForReview(allocationId: $allocationId) {
      ok
      errors
      allocation {
        id
        status
      }
    }
  }
`;

export interface AllocationDetails {
  allocationAmount: null | number;
  allocationLines: AllocationLine[];
  allocationType: null | string;
  createdDate: null | string;
  debtAllocationAmount: null | number;
  displayName: null | string;
  equityAllocationAmount: null | number;
  finopsReviewDate: null | string;
  finopsReviewed: boolean | null;
  hasOverrides: boolean | null;
  hasWarnings: boolean | null;
  id: string;
  isFulfilled: boolean | null;
  numberOfFunds: null | number;
  opportunityId: null | string;
  overrideNotes: null | string;
  status: null | string;
  totalAllocated: null | number;
  useOfProceeds: null | string;
  version: null | number;
  warnings: AllocationWarning[];
}

export interface AllocationFund {
  balance: null | number;
  buyingPower: null | number;
  concentrationLimit: null | number;
  id: string;
  name: null | string;
}

export interface AllocationHistoryItem {
  allocationAmount: null | number;
  allocationType: null | string;
  createdDate: null | string;
  debtAllocationAmount: null | number;
  equityAllocationAmount: null | number;
  hasOverrides: boolean;
  hasWarnings: boolean;
  id: string;
  isFulfilled: boolean;
  numberOfFunds: null | number;
  status: null | string;
  totalAllocated: null | number;
  version: null | number;
}

export interface AllocationLine {
  allocatedAmount: null | number;
  allocationPercentage: null | number;
  concentrationLimitAmount: null | number;
  concentrationLimitPct: null | number;
  customAllocation: null | number;
  debtAllocatedAmount: null | number;
  equityAllocatedAmount: null | number;
  excessAmount: null | number;
  fairShareAmount: null | number;
  fairShareDebtAmount: null | number;
  fairShareEquityAmount: null | number;
  fund: AllocationFund | null;
  fundId: null | string;
  id: string;
  isCapped: boolean | null;
  isExcluded: boolean | null;
  isOverLimit: boolean | null;
  overrideType: null | string;
}

export interface AllocationWarning {
  fund: { id: string; name: null | string } | null;
  fundId: null | string;
  id: string;
  message: null | string;
  severity: null | string;
  warningType: null | string;
}

export interface GetAllocationDetailsResponse {
  node: AllocationDetails | null;
}

export interface GetAllocationHistoryResponse {
  allocationHistory: AllocationHistoryItem[];
}

export interface SubmitForReviewResponse {
  submitForReview: {
    allocation: { id: string; status: null | string } | null;
    errors: string[];
    ok: boolean;
  };
}

// Re-export formatters from lib/utils for convenience
export { formatCurrency, formatPercent as formatPercentage };

export const formatDate = (dateString: null | string | undefined): string => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-US', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

export const getStatusBadgeClass = (
  status: null | string | undefined
): string => {
  switch (status?.toLowerCase()) {
    case 'cancelled':
      return 'bg-red-200 text-red-800';
    case 'draft':
      return 'bg-orange-200 text-orange-800';
    case 'finalized':
      return 'bg-green-200 text-green-800';
    case 'in_review':
      return 'bg-blue-200 text-blue-800';
    default:
      return 'bg-gray-200 text-gray-800';
  }
};

export const formatStatus = (status: null | string | undefined): string => {
  if (!status) return '-';
  return status
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const formatAllocationType = (
  type: null | string | undefined
): string => {
  if (!type) return '-';
  return type
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};
